import React, { useEffect, useMemo, useState } from 'react';

import api from '../api/api';

import {
  AlertCircle,
  Check,
  CheckCircle2,
  Database,
  Loader2,
  Phone,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  ShieldOff,
  Trash2,
  Users,
  X,
} from 'lucide-react';

/* ========================================================================
   MOBILE NUMBER HELPERS
   ======================================================================== */
const normalizeMobile = (value) => {
  let digits = String(value ?? '').replace(/\D/g, '');

  if (!digits) return '';

  if (digits.startsWith('0092')) {
    digits = digits.slice(2);
  }

  if (digits.startsWith('92')) {
    return digits;
  }

  if (digits.startsWith('0')) {
    return `92${digits.slice(1)}`;
  }

  if (digits.length === 10 && digits.startsWith('3')) {
    return `92${digits}`;
  }

  return digits;
};

const isValidMobile = (value) => {
  const normalized = normalizeMobile(value);

  return (
    normalized.length >= 10 &&
    normalized.length <= 15
  );
};

const formatMobile = (value) => {
  const normalized = normalizeMobile(value);

  if (
    normalized.startsWith('92') &&
    normalized.length === 12
  ) {
    return `+${normalized}`;
  }

  return normalized || '—';
};

/* ========================================================================
   STATUS BADGE
   ======================================================================== */
const BlockedBadge = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-[9px] font-extrabold text-red-700">
    <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
    Blocked
  </span>
);

/* ========================================================================
   STAT CARD
   ======================================================================== */
const StatCard = ({
  label,
  value,
  note,
  icon: Icon,
  tone = 'dark',
}) => {
  const themes = {
    dark: {
      card:
        'border-slate-200 bg-slate-950 shadow-[0_18px_45px_rgba(15,23,42,0.12)]',
      glow: 'bg-[#F6C453]/12',
      icon:
        'border-[#F6C453]/20 bg-[#F6C453]/10 text-[#F6C453]',
      accent: 'text-[#F6C453]',
      value: 'text-white',
      note: 'text-slate-400',
      line: 'bg-[#F6C453]',
    },

    red: {
      card:
        'border-red-100 bg-gradient-to-br from-white via-white to-red-50/70 shadow-[0_18px_45px_rgba(239,68,68,0.08)]',
      glow: 'bg-red-300/15',
      icon:
        'border-red-200 bg-red-50 text-red-600',
      accent: 'text-red-600',
      value: 'text-slate-950',
      note: 'text-slate-500',
      line: 'bg-red-500',
    },
  };

  const theme =
    themes[tone] || themes.dark;

  return (
    <div
      className={`group relative min-h-[155px] overflow-hidden rounded-[26px] border px-5 py-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_55px_rgba(15,23,42,0.14)] sm:px-6 sm:py-6 ${theme.card}`}
    >
      <div
        className={`absolute -right-14 -top-14 h-40 w-40 rounded-full blur-3xl transition-transform duration-500 group-hover:scale-110 ${theme.glow}`}
      />

      <div className="absolute right-5 top-5 opacity-[0.05] transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110">
        <Icon size={88} strokeWidth={1.2} />
      </div>

      <div className="relative z-10 flex h-full items-center justify-between gap-5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={`h-1.5 w-1.5 rounded-full ${theme.line}`}
            />

            <p
              className={`text-[9px] font-black uppercase tracking-[0.2em] ${theme.accent}`}
            >
              {label}
            </p>
          </div>

          <p
            className={`mt-4 text-[44px] font-black leading-none tracking-[-0.05em] sm:text-[50px] ${theme.value}`}
          >
            {value}
          </p>

          <p
            className={`mt-3 max-w-[390px] text-[11px] font-medium leading-5 ${theme.note}`}
          >
            {note}
          </p>
        </div>

        <div
          className={`flex h-[62px] w-[62px] shrink-0 items-center justify-center rounded-[18px] border shadow-sm transition-all duration-300 group-hover:scale-105 ${theme.icon}`}
        >
          <Icon size={28} strokeWidth={2.2} />
        </div>
      </div>

      <div
        className={`absolute bottom-0 left-0 h-[3px] w-full opacity-80 ${theme.line}`}
      />
    </div>
  );
};

/* ========================================================================
   MAIN COMPONENT
   ======================================================================== */
const BlockedNumbers = () => {
  const [mobile, setMobile] = useState('');
  const [records, setRecords] = useState([]);

  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [notice, setNotice] = useState(null);

  const showNotice = (type, message) => {
    setNotice({
      type,
      message,
    });
  };

  /* ======================================================================
     LOAD BLOCKED NUMBERS
     ====================================================================== */
  const loadNumbers = async () => {
    setLoading(true);

    try {
      const response = await api.get(
        '/api/blocked-numbers',
        {
          params: {
            search: search.trim(),
          },
        }
      );

      const data = response.data || {};

      setRecords(
        Array.isArray(data.records)
          ? data.records
          : []
      );
    } catch (error) {
      showNotice(
        'error',
        error?.response?.data?.message ||
          'Blocked numbers could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNumbers();
  }, []);

  /* ======================================================================
     FILTERED RECORDS
     ====================================================================== */
  const filteredRecords = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return records;
    }

    return records.filter((item) => {
      const number = String(
        item.mobileNumber || ''
      ).toLowerCase();

      return number.includes(query);
    });
  }, [records, search]);

  /* ======================================================================
     ADD BLOCKED NUMBER
     ====================================================================== */
  const handleAdd = async (event) => {
    event.preventDefault();

    const normalized =
      normalizeMobile(mobile);

    if (!normalized) {
      showNotice(
        'error',
        'Please enter a mobile number.'
      );
      return;
    }

    if (!isValidMobile(normalized)) {
      showNotice(
        'error',
        'Please enter a valid mobile number.'
      );
      return;
    }

    setSaving(true);

    try {
      const response = await api.post(
        '/api/blocked-numbers',
        {
          mobileNumber: normalized,
        }
      );

      const data = response.data || {};

      if (data.record) {
        setRecords((prev) => [
          data.record,
          ...prev.filter(
            (item) =>
              item.mobileNumber !==
              data.record.mobileNumber
          ),
        ]);
      } else {
        await loadNumbers();
      }

      setMobile('');

      showNotice(
        'success',
        `${formatMobile(normalized)} has been blocked successfully.`
      );
    } catch (error) {
      showNotice(
        'error',
        error?.response?.data?.message ||
          'The mobile number could not be blocked.'
      );
    } finally {
      setSaving(false);
    }
  };

  /* ======================================================================
     REMOVE BLOCK
     ====================================================================== */
  const handleDelete = async (id, number) => {
    const confirmed = window.confirm(
      `Unblock ${formatMobile(
        number
      )}? This number will be allowed to spin again.`
    );

    if (!confirmed) {
      return;
    }

    setDeletingId(id);

    try {
      await api.delete(
        `/api/blocked-numbers/${id}`
      );

      setRecords((prev) =>
        prev.filter(
          (item) => item.id !== id
        )
      );

      showNotice(
        'success',
        `${formatMobile(
          number
        )} has been unblocked successfully.`
      );
    } catch (error) {
      showNotice(
        'error',
        error?.response?.data?.message ||
          'The mobile number could not be unblocked.'
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-full bg-[#f6f7fa] p-3 sm:p-5 lg:p-6">
      <div className="mx-auto max-w-[1500px] space-y-5">

        {/* ==================================================================
            HEADER
        =================================================================== */}
        <section className="relative overflow-hidden rounded-[28px] bg-slate-950 px-5 py-6 text-white shadow-[0_20px_60px_rgba(15,23,42,0.13)] sm:px-7 sm:py-7 lg:px-8">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-red-500/10 blur-3xl" />

          <div className="absolute -bottom-28 left-[40%] h-72 w-72 rounded-full bg-[#F6C453]/10 blur-3xl" />

          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.07] px-3 py-1.5 text-[9px] font-extrabold uppercase tracking-[0.2em] text-red-300">
                <ShieldOff size={12} />
                Access Control
              </div>

              <div className="mt-4 flex items-start gap-4">
                <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.07] text-red-300 sm:flex">
                  <Phone size={25} />
                </div>

                <div>
                  <h1 className="text-[26px] font-black leading-tight tracking-[-0.03em] sm:text-3xl lg:text-[34px]">
                    Blocked Numbers
                  </h1>

                  <p className="mt-2 max-w-xl text-[11px] leading-5 text-slate-400">
                    Manage mobile numbers that are not allowed to participate in the Spin & Win campaign.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex w-full max-w-[320px] items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] p-3.5 backdrop-blur-sm lg:w-[300px]">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-500/15 text-red-300">
                <ShieldCheck size={21} />
              </div>

              <div>
                <p className="text-[9px] font-extrabold uppercase tracking-[0.16em] text-slate-400">
                  Protection status
                </p>

                <p className="mt-1 text-sm font-extrabold text-white">
                  Active blocking
                </p>

                <p className="mt-0.5 text-[10px] text-slate-400">
                  Backend enforced
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ==================================================================
            STAT
        =================================================================== */}
        <section className="grid grid-cols-1 lg:grid-cols-2">
          <StatCard
            label="Blocked Numbers"
            value={records.length}
            note="Total mobile numbers currently blocked from participating in the campaign."
            icon={ShieldOff}
            tone="red"
          />
        </section>

        {/* ==================================================================
            ADD NUMBER
        =================================================================== */}
        <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_10px_36px_rgba(15,23,42,0.045)] sm:p-5 lg:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-600">
                  <Phone size={18} />
                </div>

                <div>
                  <p className="text-[9px] font-extrabold uppercase tracking-[0.17em] text-red-500">
                    Block number
                  </p>

                  <h2 className="mt-0.5 text-base font-black text-slate-900">
                    Add mobile number
                  </h2>
                </div>
              </div>

              <p className="mt-3 max-w-xl text-[11px] leading-5 text-slate-500">
                Enter a mobile number and press Enter or click the button to block it from future spins.
              </p>
            </div>

            <div className="w-full lg:max-w-[620px]">
              <form
                onSubmit={handleAdd}
                className="flex flex-col gap-2 sm:flex-row"
              >
                <div className="relative flex-1">
                  <Phone
                    size={16}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    type="tel"
                    value={mobile}
                    onChange={(event) =>
                      setMobile(
                        event.target.value
                      )
                    }
                    placeholder="Enter mobile number"
                    autoComplete="off"
                    className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-xs font-semibold text-slate-700 outline-none transition focus:border-red-300 focus:bg-white focus:ring-2 focus:ring-red-50"
                  />
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 text-[10px] font-extrabold text-white shadow-[0_12px_25px_rgba(15,23,42,0.14)] transition-all hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  {saving ? (
                    <Loader2
                      size={15}
                      className="animate-spin"
                    />
                  ) : (
                    <Plus size={15} />
                  )}

                  {saving
                    ? 'Blocking...'
                    : 'Block Number'}
                </button>
              </form>

              <p className="mt-2 text-[9px] font-medium text-slate-400">
                Accepted formats: 03001234567, 923001234567, or +923001234567
              </p>
            </div>
          </div>
        </section>

        {/* ==================================================================
            NOTICE
        =================================================================== */}
        {notice && (
          <div
            className={`flex items-start gap-3 rounded-2xl border px-4 py-3 shadow-sm ${
              notice.type === 'error'
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
          >
            {notice.type === 'error' ? (
              <AlertCircle
                size={17}
                className="mt-0.5 shrink-0"
              />
            ) : (
              <CheckCircle2
                size={17}
                className="mt-0.5 shrink-0"
              />
            )}

            <p className="flex-1 text-[11px] font-semibold leading-5">
              {notice.message}
            </p>

            <button
              type="button"
              onClick={() =>
                setNotice(null)
              }
              className="rounded-lg p-1 hover:bg-black/5"
            >
              <X size={15} />
            </button>
          </div>
        )}

        {/* ==================================================================
            BLOCKED NUMBER REGISTER
        =================================================================== */}
        <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_10px_36px_rgba(15,23,42,0.045)]">
          <div className="flex flex-col gap-4 border-b border-slate-100 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-600">
                <Database size={18} />
              </div>

              <div>

                <h2 className="mt-0.5 text-base font-black text-slate-900">
                  Blocked mobile numbers
                </h2>
              </div>
            </div>

            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <div className="relative min-w-[250px]">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  value={search}
                  onChange={(event) =>
                    setSearch(
                      event.target.value
                    )
                  }
                  placeholder="Search mobile number..."
                  className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-[10px] font-semibold text-slate-700 outline-none transition focus:border-red-300 focus:bg-white focus:ring-2 focus:ring-red-50"
                />
              </div>

              <button
                type="button"
                onClick={
                  loadNumbers
                }
                disabled={loading}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 text-[10px] font-extrabold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2
                    size={14}
                    className="animate-spin"
                  />
                ) : (
                  <RefreshCw
                    size={14}
                  />
                )}

                Refresh
              </button>
            </div>
          </div>

          {!filteredRecords.length ? (
            <div className="flex flex-col items-center justify-center px-5 py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <Users size={25} />
              </div>

              <p className="mt-4 text-sm font-black text-slate-700">
                No blocked numbers found
              </p>

              <p className="mt-1 max-w-md text-[11px] leading-5 text-slate-400">
                Add a mobile number above to prevent it from participating in future spins.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[780px] w-full text-left">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    <th className="px-5 py-3 text-[8px] font-extrabold uppercase tracking-[0.15em] text-slate-400">
                      #
                    </th>

                    <th className="px-5 py-3 text-[8px] font-extrabold uppercase tracking-[0.15em] text-slate-400">
                      Mobile Number
                    </th>

                    <th className="px-5 py-3 text-[8px] font-extrabold uppercase tracking-[0.15em] text-slate-400">
                      Added On
                    </th>

                    <th className="px-5 py-3 text-[8px] font-extrabold uppercase tracking-[0.15em] text-slate-400">
                      Status
                    </th>

                    <th className="px-5 py-3 text-right text-[8px] font-extrabold uppercase tracking-[0.15em] text-slate-400">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {filteredRecords.map(
                    (item, index) => (
                      <tr
                        key={
                          item.id ||
                          item.mobileNumber
                        }
                        className="transition hover:bg-slate-50/70"
                      >
                        <td className="px-5 py-4 text-[10px] font-black text-slate-400">
                          {index + 1}
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-500">
                              <Phone
                                size={15}
                              />
                            </div>

                            <span className="font-mono text-[11px] font-extrabold tracking-wide text-slate-800">
                              {formatMobile(
                                item.mobileNumber
                              )}
                            </span>
                          </div>
                        </td>

                        <td className="px-5 py-4 text-[10px] font-semibold text-slate-500">
                          {item.createdAt
                            ? new Date(
                                item.createdAt
                              ).toLocaleString()
                            : '—'}
                        </td>

                        <td className="px-5 py-4">
                          <BlockedBadge />
                        </td>

                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() =>
                              handleDelete(
                                item.id,
                                item.mobileNumber
                              )
                            }
                            disabled={
                              deletingId ===
                              item.id
                            }
                            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-red-100 bg-white px-3 text-[9px] font-extrabold text-red-600 transition hover:border-red-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {deletingId ===
                            item.id ? (
                              <Loader2
                                size={13}
                                className="animate-spin"
                              />
                            ) : (
                              <Trash2
                                size={13}
                              />
                            )}

                            Unblock
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default BlockedNumbers;